import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, AlertTriangle, Sparkles } from 'lucide-react';
import './PolandHungarySlide.css';

// Import images
import tuskWinImg from '../images/images for slides/donald tust win_1.png';
import cpacHungaryImg from '../images/images for slides/CPAC Hungary Event.jpg';

function PolandHungarySlide({ showNotes }) {
  const [activeTab, setActiveTab] = useState('poland');

  const polandData = {
    flag: '🇵🇱',
    name: 'Polen',
    period: 'PiS (2015-2023)',
    points: [
      { icon: '📺', text: 'Öffentliche Medien (TVP) wurden zum Propaganda-Instrument' },
      { icon: '⚖️', text: 'Justizreformen zur Kontrolle der Richter' },
      { icon: '🎯', text: 'Tägliche Hetze gegen Migranten und LGBTQ-Personen' },
    ],
    contradiction: {
      icon: '⚠️',
      title: 'Widerspruch',
      text: '"Visa-Skandal" – Beamte verkauften heimlich Visa an Migranten'
    },
    goodNews: {
      title: 'Aber: Die Demokratie hat sich gewehrt!',
      text: 'Ende 2023 wurde die PiS abgewählt. Ein Bündnis unter Donald Tusk gewann die Wahl. Die Zivilgesellschaft hat sich das Land zurückgeholt.',
      icon: '✅'
    }
  };

  const hungaryData = {
    flag: '🇭🇺',
    name: 'Ungarn',
    period: 'Orbán (seit 2010)',
    points: [
      { icon: '🏛️', text: 'Selbstbezeichnung als "illiberale Demokratie"' },
      { icon: '📡', text: 'Fast vollständige Kontrolle über Medien und Justiz' },
      { icon: '🌍', text: 'Vorbild für die internationale Rechte' },
    ],
    international: [
      { icon: '🇺🇸', text: 'CPAC Hungary: Amerikanische Konservative treffen sich in Budapest' },
      { icon: '📺', text: 'Tucker Carlson sendete tagelang aus Ungarn und lobte das Modell' },
    ],
    connection: 'Beide Regierungen erhielten jahrelang EU-Gelder trotz Demokratieabbau.'
  };

  return (
    <div className="slide poland-hungary-slide">
      <div className="ph-layout">
        {/* Header */}
        <div className="ph-header">
          <motion.span
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="section-label"
          >
            An der Macht (1)
          </motion.span>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="slide-title"
          >
            Polen & Ungarn
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="slide-subtitle"
          >
            Was passiert, wenn Rechtsextreme wirklich regieren?
          </motion.p>
        </div>

        {/* Tab Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="tab-navigation"
        >
          <button
            className={`tab-btn ${activeTab === 'poland' ? 'active' : ''}`}
            onClick={() => setActiveTab('poland')}
          >
            <span className="tab-flag">🇵🇱</span>
            <span>Polen</span>
            <span className="tab-badge success">Hoffnung</span>
          </button>
          <button
            className={`tab-btn ${activeTab === 'hungary' ? 'active' : ''}`}
            onClick={() => setActiveTab('hungary')}
          >
            <span className="tab-flag">🇭🇺</span>
            <span>Ungarn</span>
            <span className="tab-badge danger">Schwierig</span>
          </button>
        </motion.div>

        {/* Content Area */}
        <div className="ph-content">
          <AnimatePresence mode="wait">
            {activeTab === 'poland' ? (
              <motion.div
                key="poland"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3 }}
                className="country-content"
              >
                <div className="content-grid">
                  {/* Left Column */}
                  <div className="content-left">
                    <div className="country-header">
                      <span className="country-flag">{polandData.flag}</span>
                      <div>
                        <h3>{polandData.name}</h3>
                        <span className="period">{polandData.period}</span>
                      </div>
                    </div>

                    <div className="points-list">
                      {polandData.points.map((point, index) => (
                        <div key={index} className="point-item">
                          <span className="point-icon">{point.icon}</span>
                          <span className="point-text">{point.text}</span>
                        </div>
                      ))}
                    </div>

                    {/* Contradiction Box */}
                    <div className="contradiction-box">
                      <div className="contradiction-header">
                        <AlertTriangle size={16} />
                        <span>{polandData.contradiction.title}</span>
                      </div>
                      <p>{polandData.contradiction.text}</p>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="content-right">
                    {/* Image */}
                    <div className="country-image">
                      <img src={tuskWinImg} alt="Donald Tusk Wahlsieg 2023" className="country-photo" />
                    </div>

                    {/* Good News Box */}
                    <div className="good-news-box">
                      <div className="good-news-header">
                        <Sparkles size={20} className="sparkle-icon" />
                        <span>{polandData.goodNews.title}</span>
                      </div>
                      <p>{polandData.goodNews.text}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="hungary"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="country-content"
              >
                <div className="content-grid">
                  {/* Left Column */}
                  <div className="content-left">
                    <div className="country-header">
                      <span className="country-flag">{hungaryData.flag}</span>
                      <div>
                        <h3>{hungaryData.name}</h3>
                        <span className="period">{hungaryData.period}</span>
                      </div>
                    </div>

                    <div className="points-list">
                      {hungaryData.points.map((point, index) => (
                        <div key={index} className="point-item">
                          <span className="point-icon">{point.icon}</span>
                          <span className="point-text">{point.text}</span>
                        </div>
                      ))}
                    </div>

                    {/* International Box */}
                    <div className="international-box">
                      <h4>Internationale Vernetzung</h4>
                      <div className="international-list">
                        {hungaryData.international.map((item, index) => (
                          <div key={index} className="international-item">
                            <span className="int-icon">{item.icon}</span>
                            <span className="int-text">{item.text}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="content-right">
                    {/* Image */}
                    <div className="country-image">
                      <img src={cpacHungaryImg} alt="CPAC Hungary Event" className="country-photo" />
                    </div>

                    {/* Quote Box */}
                    <div className="quote-box">
                      <span className="quote-mark">"</span>
                      <p className="quote-text">Illiberale Demokratie</p>
                      <p className="quote-author">– Orbáns Selbstbezeichnung</p>
                      <p className="quote-explanation">
                        Das bedeutet: Wahlen ja, aber keine echten Freiheiten.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Connection Footer */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="connection-footer"
          >
            <div className="connection-content">
              <span className="connection-icon">🇪🇺</span>
              <span className="connection-text">
                {hungaryData.connection}
              </span>
            </div>
          </motion.div>
        </div>
      </div>

      {showNotes && (
        <div className="speaker-notes">
          <strong>📚 Zusatzinfos für Leser:</strong><br/>
          <strong>Polen (Die Hoffnung):</strong> Die PiS-Regierung scheiterte an ihrer eigenen Heuchelei (Visa-Skandal: Hetze gegen Migranten, aber Verkauf von Visa) 
          und Übermut (totales Abtreibungsverbot). Die Zivilgesellschaft hat das System an der Urne korrigiert.<br/><br/>
          <strong>Ungarn (Das Warnsignal):</strong> Orbán kann kaum noch abgewählt werden, weil er das System umgebaut hat:<br/>
          • <strong>Gerrymandering:</strong> Wahlkreise wurden zugunsten der Regierungspartei Fidesz manipuliert.<br/>
          • <strong>Medienmonopol:</strong> Auf dem Land gibt es keine freie Presse mehr.<br/>
          In Ungarn wurde das "Betriebssystem" der Demokratie so verändert, dass die Regierung fast unantastbar ist.
        </div>
      )}
    </div>
  );
}

export default PolandHungarySlide;
